import {
  createMemberFlowTemplate,
  getMemberFlow,
  listPuckPages,
  upsertMemberFlow,
  type MemberFlowStep,
} from "@/api/cms-puck/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";

const FLOW_KEY = ["cms-puck", "member-flow"] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;
const NONE_VALUE = "__none__";

/** Clés + fallbacks — ne pas résoudre cmsNew() au niveau module (bundle i18n async). */
const STEPS: {
  step: MemberFlowStep;
  labelKey: CmsNewKey;
  fallback: string;
  route: string;
}[] = [
  {
    step: "LOGIN",
    labelKey: "cms.new.blocks.memberLogin.label",
    fallback: "Connexion",
    route: "/connexion",
  },
  {
    step: "SIGNUP",
    labelKey: "cms.new.blocks.memberSignup.label",
    fallback: "Inscription",
    route: "/inscription",
  },
  {
    step: "PROFILE",
    labelKey: "cms.new.blocks.memberProfile.label",
    fallback: "Profil",
    route: "/profil",
  },
  {
    step: "FAVORITES",
    labelKey: "cms.new.blocks.memberFavorites.label",
    fallback: "Favoris",
    route: "/favorites",
  },
];

export function memberRoleBadgeLabel(step: MemberFlowStep): string {
  const meta = STEPS.find((s) => s.step === step);
  return meta ? cmsNew(meta.labelKey, meta.fallback) : step;
}

function FlowRow({
  step: _step,
  label,
  route,
  value,
  templatePages,
  onAssign,
  onCreateTemplate,
  saving,
  creating,
}: {
  step: MemberFlowStep;
  label: string;
  route: string;
  value: string | null;
  templatePages: { id: string; name: string; status: string }[];
  onAssign: (pageId: string | null) => void;
  onCreateTemplate: () => void;
  saving: boolean;
  creating: boolean;
}) {
  const cmsNew = useCmsNew();
  const navigate = useNavigate();
  const assigned = templatePages.find((p) => p.id === value);
  const isDraft = assigned && assigned.status !== "PUBLISHED";

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <code className="text-[11px] text-slate-400">{route}</code>
      </div>
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(next) =>
          onAssign(next === NONE_VALUE ? null : next)
        }
        disabled={saving || creating}
      >
        <SelectTrigger>
          <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.aucune_page_classique", "Aucune (page classique)")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>{cmsNew("cms.new.auto.selectItem.aucune_page_classique", "Aucune (page classique)")}</SelectItem>
          {templatePages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {page.name}
              {page.status !== "PUBLISHED" ? ` (${puckPageStatusLabel(cmsNew, page.status)})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isDraft ? (
        <p className="text-xs text-amber-700">
          {cmsPhrase(cmsNew, "draftPublishHint")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            onClick={() => navigate(`/cms/advanced/${value}`)}
          >
            <ExternalLink size={14} />
            {cmsPhrase(cmsNew, "editTemplate")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1"
            disabled={creating}
            onClick={onCreateTemplate}
          >
            <Plus size={14} />
            {creating ? cmsPhrase(cmsNew, "creating") : cmsPhrase(cmsNew, "createTemplate")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CMSAdvancedParcoursMembre() {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const flowQuery = useQuery({ queryKey: FLOW_KEY, queryFn: getMemberFlow });
  const pagesQuery = useQuery({
    queryKey: PAGES_KEY,
    queryFn: () => listPuckPages(),
  });

  const saveMutation = useMutation({
    mutationFn: upsertMemberFlow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.parcours_membre_enregistre", "Parcours membre enregistré"),
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.auto.title.impossible_denregistrer_la_liaison", "Impossible d'enregistrer la liaison"),
        variant: "error",
      });
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: createMemberFlowTemplate,
    onSuccess: ({ page }) => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.template_membre_cree", "Template membre créé"),
        variant: "success",
        duration: 1600,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : cmsPhrase(cmsNew, "createTemplateFailed");
      toast({ title: message, variant: "error" });
    },
  });

  const bindings = flowQuery.data ?? [];
  const pages = pagesQuery.data ?? [];
  const templatePages = pages.filter((p) => (p.memberFlowRoles?.length ?? 0) > 0);

  const pageIdFor = (step: MemberFlowStep) =>
    bindings.find((b) => b.step === step)?.pageId ?? null;

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.parcours_membre", "Parcours membre")}
      subtitle={cmsNew(
        "cms.new.memberFlow.subtitle",
        "Templates CMS avancé pour connexion, inscription, profil et favoris. Les URLs système restent inchangées.",
      )}
    >
      <div className="space-y-6 px-6 py-4">
        <p className="text-sm text-slate-600">{cmsPhrase(cmsNew, "memberFlowIntro")}</p>

        {flowQuery.isLoading || pagesQuery.isLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {STEPS.map(({ step, labelKey, fallback, route }) => (
              <FlowRow
                key={step}
                step={step}
                label={cmsNew(labelKey, fallback)}
                route={route}
                value={pageIdFor(step)}
                templatePages={templatePages}
                saving={saveMutation.isPending}
                creating={createTemplateMutation.isPending}
                onAssign={(pageId) => saveMutation.mutate({ step, pageId })}
                onCreateTemplate={() => createTemplateMutation.mutate({ step })}
              />
            ))}
          </div>
        )}

        <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          <p className="font-medium text-slate-800">{cmsNew("cms.new.auto.text.blocs_disponibles", "Blocs disponibles")}</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>
              <strong>{cmsNew("cms.new.auto.text.connexion", "Connexion")}</strong> / <strong>{cmsNew("cms.new.auto.text.inscription", "Inscription")}</strong> — {cmsNew("cms.new.auto.text.connexion_inscription_desc", "formulaires avec image latérale optionnelle (CMS legacy)")}
            </li>
            <li>
              <strong>{cmsNew("cms.new.auto.text.profil", "Profil")}</strong> — {cmsNew("cms.new.auto.text.profil_desc", "onglets compte, mot de passe, abonnement, commandes")}
            </li>
            <li>
              <strong>{cmsNew("cms.new.auto.text.favoris", "Favoris")}</strong> — {cmsNew("cms.new.auto.text.favoris_desc", "grille d'œuvres favorites avec filtres")}
            </li>
          </ul>
          <p className="mt-2">
            {cmsNew("cms.new.auto.text.auth_legacy_note", "Le CMS legacy « Authentification » (/cms/authentification) alimente encore les textes et images de connexion/inscription.")}
          </p>
        </div>
      </div>
    </CmsAdminShell>
  );
}
